import type { HilPresentationRequestEnvelope, HilPresentationAction } from '@bizfirst/hil-types';
import type { InboxItemRecord, HilEventLogRecord } from './hilInboxTypes';

function toPresentationType(actionType: string): 'form' | 'chat' | 'approval' {
  const t = actionType.toLowerCase();
  if (t === 'form' || t === 'chat_with_form') return 'form';
  if (t === 'chat') return 'chat';
  return 'approval';
}

function defaultActions(presentationType: string): HilPresentationAction[] {
  if (presentationType === 'form') {
    return [
      { ActionKey: 'submit', Label: 'Submit', Decision: 'submitted', IsPrimary: true, Style: 'primary', DisplayOrder: 1 },
      { ActionKey: 'cancel', Label: 'Cancel', Decision: 'cancelled', Style: 'neutral', DisplayOrder: 2 },
    ];
  }
  if (presentationType === 'chat') {
    return [
      { ActionKey: 'reply', Label: 'Send Reply', Decision: 'replied', IsPrimary: true, Style: 'primary', DisplayOrder: 1 },
    ];
  }
  return [
    { ActionKey: 'approve', Label: 'Approve', Decision: 'approved', IsPrimary: true, Style: 'primary', DisplayOrder: 1 },
    { ActionKey: 'reject',  Label: 'Reject',  Decision: 'rejected',  RequiresReason: true, Style: 'danger',   DisplayOrder: 2 },
    { ActionKey: 'abstain', Label: 'Abstain', Decision: 'abstained', Style: 'neutral', DisplayOrder: 3 },
  ];
}

/** Extracts FormID from the stored HilPresentationRequestEnvelope JSON (PascalCase, set by backend). */
function extractFormIDFromRequest(hilPresentationRequest: string | undefined): number | undefined {
  if (!hilPresentationRequest) return undefined;
  try {
    const parsed = JSON.parse(hilPresentationRequest);
    const formID = parsed?.Presentations?.[0]?.FormID;
    return typeof formID === 'number' ? formID : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Maps a backend InboxItem + optional HilEventLog to a v1.3 HilPresentationRequestEnvelope.
 * Pure function — no side effects, no HTTP.
 */
export function mapInboxItemToV13Envelope(
  item: InboxItemRecord,
  log: HilEventLogRecord | null,
  baseUrl: string
): HilPresentationRequestEnvelope {
  const pt = toPresentationType(item.ActionType ?? 'inbox');
  const resumeURL = `${baseUrl}/api/v1/ai/inbox-items/${item.InboxItemID}/act`;

  // Prefer PrimaryFormID from event log; fall back to FormID stored in the presentation request.
  const formID = log?.PrimaryFormID ?? extractFormIDFromRequest(item.HilPresentationRequest);

  return {
    Version: '1.3',
    Session: {
      EngageSessionID: item.EngageSessionID,
      ResumeCallbackURL: resumeURL,
      CorrelationID: item.ExternalResumeToken ?? item.ResID ?? `inbox-${item.InboxItemID}`,
      NodeKey: log?.NodeKey ?? '',
      ProcessElementID: item.ProcessElementExecutionID?.toString(),
    },
    HilType: pt,
    Title: item.Title,
    Description: item.Description,
    Deadline: item.DueAt ?? log?.ExpiresAt,
    Assignees: [{ ActorID: item.AssignedToActorID, Channel: 'web' }],
    Channels: [{ ChannelType: 'web' }],
    Presentations: [
      {
        Order: 1,
        PresentationType: pt,
        Title: item.Title,
        PromptMessage: item.Description ?? '',
        FormID: formID,
        Actions: defaultActions(pt),
      },
    ],
    ReplyOptions: { AllowComment: true },
  };
}
